import React, { useState, useEffect, useRef } from "react";
import SpeechRecognition, {
    useSpeechRecognition,
} from "react-speech-recognition";
import { Icon } from "@/components/Icon";
import { Tooltip } from "@/components/Tooltip";
import { useTranslation } from "react-i18next";

interface MicrophoneButtonProps {
    onResult: (text: string) => void;
    onTranscriptChange?: (text: string) => void;
    onListeningChange?: (isListening: boolean) => void;
    onCancel?: () => void;
    className?: string;
    iconSize?: number;
}

export const MicrophoneButton: React.FC<MicrophoneButtonProps> = ({
    onResult,
    onTranscriptChange,
    onListeningChange,
    onCancel,
    className = "",
    iconSize = 18,
}) => {
    const { t } = useTranslation("global");
    const {
        transcript,
        listening,
        resetTranscript,
        browserSupportsSpeechRecognition,
    } = useSpeechRecognition();

    const [isRecordingSession, setIsRecordingSession] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const lastListening = useRef(listening);

    useEffect(() => {
        if (onListeningChange) {
            onListeningChange(isRecordingSession);
        }
    }, [isRecordingSession, onListeningChange]);

    useEffect(
        () => () => {
            if (isRecordingSession) SpeechRecognition.abortListening();
        },
        [isRecordingSession]
    );

    // Handle auto-stop (e.g. silence timeout from browser)
    useEffect(() => {
        if (isRecordingSession && !isPaused) {
            if (lastListening.current === true && listening === false) {
                // Browser stopped listening automatically
                setIsPaused(true);
                if (transcript) {
                    onResult(transcript);
                    resetTranscript();
                }
            }
        }
        lastListening.current = listening;
    }, [
        listening,
        isRecordingSession,
        isPaused,
        transcript,
        onResult,
        resetTranscript,
    ]);

    useEffect(() => {
        if (isRecordingSession && !isPaused && onTranscriptChange) {
            onTranscriptChange(transcript);
        }
    }, [transcript, isRecordingSession, isPaused, onTranscriptChange]);

    if (!browserSupportsSpeechRecognition) {
        return null;
    }

    const startSession = () => {
        setIsRecordingSession(true);
        setIsPaused(false);
        resetTranscript();
        SpeechRecognition.startListening({
            continuous: true,
            language: "es-ES",
        });
    };

    const handlePause = () => {
        SpeechRecognition.stopListening();
        setIsPaused(true);
        if (transcript) {
            onResult(transcript);
            resetTranscript();
        }
    };

    const handleResume = () => {
        setIsPaused(false);
        resetTranscript();
        SpeechRecognition.startListening({
            continuous: true,
            language: "es-ES",
        });
    };

    const handleStop = () => {
        SpeechRecognition.stopListening();
        setIsRecordingSession(false);
        setIsPaused(false);
        if (transcript) {
            onResult(transcript);
        }
        resetTranscript();
    };

    const handleCancel = () => {
        SpeechRecognition.abortListening();
        setIsRecordingSession(false);
        setIsPaused(false);
        resetTranscript();
        if (onCancel) onCancel();
    };

    const isChatComposer = className.includes("chat-composer-btn");

    return (
        <div
            className={`flex items-center transition-all duration-300 overflow-hidden ${
                isRecordingSession
                    ? `max-w-[150px] bg-rose-50 border border-rose-200 dark:border-rose-900/50 dark:bg-rose-950/40 pl-2 pr-1 gap-0.5 rounded-full ${isChatComposer ? "min-h-[40px]" : "py-1"} ${className.replace("!p-0", "").replace("p-1.5", "").replace("btn secondary chat-composer-btn w-9 shrink-0", "").replace("btn secondary chat-composer-btn", "")}`
                    : "bg-transparent border-transparent p-0 gap-0"
            }`}
        >
            {isRecordingSession ? (
                <>
                    <div
                        className={`w-2 h-2 mx-1 rounded-full shrink-0 ${isPaused ? "bg-rose-300 dark:bg-rose-700" : "bg-rose-500 dark:bg-rose-400 animate-pulse"}`}
                    />

                    <Tooltip title={t("common.cancel")}>
                        <button
                            type="button"
                            onClick={handleCancel}
                            className="px-1 rounded-full text-rose-600 hover:text-rose-800 dark:text-zinc-400 dark:hover:text-rose-300 dark:hover:bg-rose-900/50 transition-colors shrink-0"
                        >
                            <Icon value="trash" size={16} />
                        </button>
                    </Tooltip>

                    {isPaused ? (
                        <Tooltip title={t("recording.resume")}>
                            <button
                                type="button"
                                onClick={handleResume}
                                className="px-1 rounded-full text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-600/50 transition-colors shrink-0"
                            >
                                <Icon value="mic" size={16} />
                            </button>
                        </Tooltip>
                    ) : (
                        <Tooltip title={t("recording.pause")}>
                            <button
                                type="button"
                                onClick={handlePause}
                                className="px-1 rounded-full text-amber-600 hover:text-amber-800 dark:text-amber-500 dark:hover:text-amber-600/50 transition-colors shrink-0"
                            >
                                <Icon value="pause" size={16} />
                            </button>
                        </Tooltip>
                    )}

                    <Tooltip title={t("recording.stop")}>
                        <button
                            type="button"
                            onClick={handleStop}
                            className="px-1 rounded-full text-rose-600 hover:text-rose-800 dark:text-rose-400 dark:hover:text-rose-600/50 transition-colors ml-0.5 shrink-0"
                        >
                            <Icon
                                value="square"
                                size={14}
                                fill="currentColor"
                                strokeWidth={2.5}
                            />
                        </button>
                    </Tooltip>
                </>
            ) : (
                <Tooltip title={t("recording.start")}>
                    <button
                        type="button"
                        onClick={startSession}
                        aria-label={t("recording.start")}
                        className={`flex items-center justify-center transition-colors cursor-pointer shrink-0 ${className}`}
                    >
                        <Icon value="mic" size={iconSize} />
                    </button>
                </Tooltip>
            )}
        </div>
    );
};
